import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import FeaturedComics from './components/FeaturedComics';
import LatestUpdates from './components/LatestUpdates';
import { categories, hotComics, newComics, comics, getComicsByCategory } from '@/mocks/comics';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [displayComics, setDisplayComics] = useState(comics);

  const handleCategorySelect = useCallback((slug: string) => {
    setActiveCategory(slug);
    setDisplayComics(getComicsByCategory(slug));
  }, []);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />

          <CategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onSelect={handleCategorySelect}
          />

          <article>
            {activeCategory === 'all' ? (
              <>
                <FeaturedComics
                  comics={hotComics.slice(0, 6)}
                  title="热门推荐"
                  subtitle="千万读者都在追的韩漫精选"
                  viewAllHref="/category/all"
                />
                <FeaturedComics
                  comics={newComics.slice(0, 6)}
                  title="新作上线"
                  subtitle="最新上架的优质韩漫作品"
                  viewAllHref="/category/all"
                />
              </>
            ) : (
              <FeaturedComics
                comics={displayComics}
                title={categories.find(c => c.slug === activeCategory)?.name || '分类'}
                subtitle={`共 ${displayComics.length} 部作品`}
                viewAllHref={`/category/${activeCategory}`}
              />
            )}

            <LatestUpdates comics={comics.slice(0, 8)} />

            <FeaturedComics
              comics={comics.filter(c => c.rating >= 9.3).slice(0, 6)}
              title="高分神作"
              subtitle="评分9.3以上，读者口碑推荐"
              viewAllHref="/category/all"
            />
          </article>

          <section className="py-16 md:py-20 bg-gradient-to-br from-primary-500 via-primary-600 to-accent-600">
            <div className="max-w-[1440px] mx-auto px-4 md:px-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-4">
                加入韩漫漫画，开启阅读之旅
              </h2>
              <p className="text-background-200 text-sm md:text-base max-w-[600px] mx-auto mb-8">
                海量正版韩漫资源，每日更新，免费章节随时可读。立即加入数百万读者，享受高品质漫画阅读体验。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="/category/all"
                  className="px-8 py-3 rounded-full bg-background-50 text-primary-600 text-base font-bold hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-book-open-line mr-1.5"></i>
                  开始阅读
                </a>
                <a
                  href="/about"
                  className="px-8 py-3 rounded-full border-2 border-background-50/50 text-background-50 text-base font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap"
                >
                  <i className="ri-information-line mr-1.5"></i>
                  了解更多
                </a>
              </div>
            </div>
          </section>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'WebPage',
                name: '韩漫漫画 - 首页',
                description: '韩漫漫画网站首页，设有免费漫画弹窗入口，提供海量正版韩漫在线阅读服务',
                url: 'https://www.manhuakr.com/',
                isPartOf: {
                  '@type': 'WebSite',
                  name: '韩漫漫画',
                  url: 'https://www.manhuakr.com/',
                },
              }),
            }}
          />
        </main>
        <Footer />
      </div>
    </>
  );
}